import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Stack, Divider, Avatar } from "@mui/material";
import { RichText } from "@payloadcms/richtext-lexical/react";
import { useTheme } from "@emotion/react";
// Custom Components
import SEO from "components/CBSEO";
import CBLoading from "components/CBLoading";
import MKTypography from "components/MKTypography";
import ShareButtons from "components/ShareButtons";
import SchemaComponent from "components/CBSchema";

// Styled Components
import { StyledMainPageContainer } from "index.styles";

// API and Utils
import { fetchBlogBySlug, getImageUrl, getAuthorAvatarUrl } from "utils/api/payloadCMS";
import { constructSchemaData } from "utils/helpers/constructSchemaData";

// Custom rich text renderer for handling inline images
const CustomRichText = ({ data }) => {
  // Function to recursively render lexical nodes
  const renderLexicalNode = (node, index = 0) => {
    if (!node) return null;

    // Handle upload/image nodes
    if (node.type === "upload" && node.value) {
      const baseUrl = process.env.REACT_APP_PAYLOAD_API_URL;
      const imageUrl = node.value.url.startsWith("http") ? node.value.url : `${baseUrl}${node.value.url}`;

      return (
        <img
          key={index}
          src={imageUrl}
          alt={node.value.alt || node.value.filename || ""}
          style={{
            maxWidth: "100%",
            height: "auto",
            margin: "1rem 0",
            borderRadius: "8px",
          }}
        />
      );
    }

    // For other node types, fall back to the default RichText component
    return null;
  };

  // Check if we have any upload nodes that need special handling
  const hasUploads = (node) => {
    if (node?.type === "upload") return true;
    if (node?.children) {
      return node.children.some((child) => hasUploads(child));
    }
    return false;
  };

  // If there are uploads, we need to handle them specially
  if (data?.root?.children && data.root.children.some(hasUploads)) {
    return (
      <div>
        {data.root.children.map((node, index) => {
          if (node.type === "upload") {
            return renderLexicalNode(node, index);
          } else {
            // For non-upload nodes, try to use the standard RichText component
            try {
              return <RichText key={index} data={{ root: { children: [node] } }} />;
            } catch (error) {
              console.log("Error rendering node:", node, error);
              return null;
            }
          }
        })}
      </div>
    );
  }

  // If no uploads, use the standard RichText component
  return <RichText data={data} />;
};

const BlogPost = () => {
  const { slug, modalityType } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) {
        setError("No slug provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const blogData = await fetchBlogBySlug(slug, modalityType);

        if (!blogData) {
          setError(`Blog post not found for slug: "${slug}"`);
          return;
        }

        setBlog(blogData);
      } catch (err) {
        setError(`Failed to load blog post: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  if (loading) return <CBLoading />;

  if (error) {
    return (
      <StyledMainPageContainer>
        <Container sx={{ textAlign: "center", py: 8 }}>
          <MKTypography variant="h4" gutterBottom>
            {error}
          </MKTypography>
          <button
            onClick={() => navigate("/blog")}
            style={{
              marginTop: "2rem",
              padding: "12px 24px",
              backgroundColor: theme.palette.accent.main,
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Back to Blog
          </button>
        </Container>
      </StyledMainPageContainer>
    );
  }

  if (!blog) return null;

  const featuredImageUrl = getImageUrl(blog.featuredImage);
  const authorAvatarUrl = getAuthorAvatarUrl(blog.author);
  // Clean the slug to avoid double slashes
  const cleanSlug = blog.slug?.replace(/^\/+/, ""); // Remove leading slashes
  const hasModality =
    cleanSlug.includes("ketamine-therapy/blog/") || cleanSlug.includes("psilocybin-therapy/blog/") || cleanSlug.includes("online-therapy/blog/");

  const blogSlug = hasModality ? `/${cleanSlug}` : `/blog/${cleanSlug}`;

  // ! canonicalUrl is wrong, for urls with modalityType, it should be: /
  const canonicalUrl = `https://www.almadelic.com${modalityType ? blogSlug : `/blog/${cleanSlug}`}`;

  // Create breadcrumbs for schema
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: blog.title, url: `/blog/${cleanSlug}` },
  ];
  const formattedModality = modalityType
    ?.split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const modalityBreadCrumbs = [
    { name: "Home", url: "/" },
    { name: `${formattedModality}`, url: `/${modalityType}` },
    { name: `${formattedModality} Blog`, url: `/${modalityType}/blog` },
    { name: blog.title, url: blogSlug },
  ];
  const dynamicBreadCrumbs = modalityType ? modalityBreadCrumbs : breadcrumbs;

  // Extract article body and calculate word count from rich text content
  const extractArticleBody = (content) => {
    if (!content) return "";
    try {
      // Handle Payload CMS Lexical rich text structure
      if (typeof content === "string") return content;

      // For Lexical editor content, we need to extract text from the JSON structure
      const extractTextFromLexical = (node) => {
        if (!node) return "";

        if (typeof node === "string") return node;

        if (Array.isArray(node)) {
          return node.map(extractTextFromLexical).join(" ");
        }

        if (typeof node === "object") {
          let text = "";

          // Handle different node types
          if (node.text) {
            text += node.text;
          }

          if (node.children) {
            text += extractTextFromLexical(node.children);
          }

          if (node.root && node.root.children) {
            text += extractTextFromLexical(node.root.children);
          }

          return text;
        }

        return "";
      };

      const extractedText = extractTextFromLexical(content);
      return extractedText.replace(/\s+/g, " ").trim();
    } catch (error) {
      return blog.description || "";
    }
  };

  const calculateWordCount = (text) => {
    if (!text) return 0;
    return text.split(/\s+/).filter((word) => word.length > 0).length;
  };

  const articleBody = extractArticleBody(blog.content);
  const wordCount = calculateWordCount(articleBody);

  // Create article data for schema
  const articleData = {
    headline: blog.title,
    authorName: blog.author?.name || "Almadelic",
    datePublished: blog.publishedDate || blog.createdAt,
    dateModified: blog.updatedAt || blog.publishedDate || blog.createdAt,
    image: featuredImageUrl,
    articleBody: articleBody,
    publisherName: "Almadelic Medical Group",
    publisherLogo: "https://d3a0rs3velfrz.cloudfront.net/logos/mainLogo.svg",
    description: blog.metaDescription || blog.description,
    mainEntityOfPage: canonicalUrl,
    wordCount: wordCount,
  };

  // Generate schema data using existing pattern
  const schemaData = constructSchemaData(canonicalUrl, dynamicBreadCrumbs, articleData);

  return (
    <StyledMainPageContainer>
      <SchemaComponent schemas={schemaData} />
      <SEO title={`${blog.metaTitle}`} description={blog.metaDescription} canonicalUrl={canonicalUrl} image={featuredImageUrl} />

      <Container sx={{ py: 4, maxWidth: "700px" }}>
        {/* Featured Image */}
        {featuredImageUrl && (
          <Box
            sx={{
              width: "100%",
              maxHeight: "500px",
              position: "relative",
              display: "inline-block",
              overflow: "hidden",
              marginBlock: "1rem",
              borderRadius: "12px",
            }}
          >
            <Box
              component="img"
              sx={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
                borderRadius: "12px",
              }}
              src={featuredImageUrl}
              alt={blog.title}
            />
          </Box>
        )}

        {/* Title */}
        <MKTypography variant="h1" component="h1" gutterBottom>
          {blog.title}
        </MKTypography>

        {/* Author and Date Info */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {authorAvatarUrl && <Avatar src={authorAvatarUrl} alt={blog.author?.name} sx={{ width: 40, height: 40 }} />}
            <Box>
              <MKTypography variant="body2" sx={{ fontSize: "16px" }}>
                By {blog.author?.name || "Almadelic"}
              </MKTypography>
              <MKTypography variant="body2" color="text.secondary" sx={{ fontSize: "16px" }}>
                Posted{" "}
                {blog.publishedDate
                  ? new Date(blog.publishedDate).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })
                  : "Unknown Date"}
              </MKTypography>
            </Box>
          </Stack>

          <ShareButtons url={canonicalUrl} title={blog.title} />
        </Stack>

        <Divider sx={{ mb: 4 }} />

        {/* Rich Text Content */}
        <Box
          sx={{
            "& p": { mb: 2, fontSize: "1rem", lineHeight: 1.7 },
            "& h2": {
              mt: 4,
              mb: 2,
              fontWeight: "bold",
              color: "#092860",
              fontSize: "1.5rem", // 24px base
              lineHeight: 1.1,
              "@media (min-width: 576px)": {
                fontSize: "1.75rem", // 28px mobile
              },
              "@media (min-width: 769px)": {
                fontSize: "2rem", // 32px tablet
              },
              "@media (min-width: 1200px)": {
                fontSize: "2.125rem", // 34px desktop
              },
            },
            "& h3": {
              mt: 3,
              mb: 2,
              fontWeight: "bold",
              color: "#092860",
              fontSize: "1.25rem", // 20px base
              lineHeight: 1.1,
              "@media (min-width: 576px)": {
                fontSize: "1.375rem", // 22px mobile
              },
              "@media (min-width: 769px)": {
                fontSize: "1.75rem", // 28px tablet
              },
            },
            "& h4": {
              mt: 3,
              mb: 2,
              fontWeight: "bold",
              color: "#092860",
              fontSize: "1.125rem", // 18px base
              lineHeight: 1.2,
            },
            "& h5": {
              mt: 2,
              mb: 2,
              fontWeight: "bold",
              color: "#092860",
              fontSize: "1rem", // 16px base
              lineHeight: 1.2,
              "@media (min-width: 769px)": {
                fontSize: "1.5rem", // 24px tablet
              },
            },
            "& h6": {
              mt: 2,
              mb: 2,
              fontWeight: "bold",
              color: "#092860",
              fontSize: "1rem", // 16px base
              lineHeight: 1.1,
              "@media (min-width: 769px)": {
                fontSize: "1.125rem", // 18px tablet
              },
              "@media (min-width: 1200px)": {
                fontSize: "1.25rem", // 20px desktop
              },
            },
            "& ul, ol": { mb: 2, pl: 3 },
            "& li": { mb: 1 },
            "& ul li::marker": { color: "#F77C3a" },
            "& ol li::marker": { color: "#F77C3a" },
            "& blockquote": {
              borderLeft: `4px solid ${theme.palette.accent.main}`,
              pl: 2,
              fontStyle: "italic",
              mb: 2,
              backgroundColor: "#f9f9f9",
              padding: "1rem",
            },
            "& a": {
              color: "#F77C3a",
              fontWeight: "bold",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            },
          }}
        >
          {blog.content ? (
            <CustomRichText data={blog.content} />
          ) : (
            <MKTypography variant="body1">No content available for this blog post.</MKTypography>
          )}
        </Box>

        {/* Keywords/Tags */}
        {blog.keywords && blog.keywords.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <MKTypography variant="h6" gutterBottom>
              <span style={{ color: theme.palette.accent.main }}>Article</span> Tags
            </MKTypography>
            <Box>
              {blog.keywords.map((keyword, index) => (
                <span
                  key={index}
                  style={{
                    display: "inline-block",
                    background: "#f0f0f0",
                    padding: "8px 16px",
                    borderRadius: "20px",
                    marginRight: "8px",
                    marginBottom: "8px",
                    fontSize: "0.875rem",
                    color: "#666",
                  }}
                >
                  {keyword}
                </span>
              ))}
            </Box>
          </Box>
        )}
      </Container>
    </StyledMainPageContainer>
  );
};

export default BlogPost;
